import {
    Action,
    IAgentRuntime,
    Memory,
    State,
    HandlerCallback,
    elizaLogger,
} from "@elizaos/core";
import { initCotiWalletProvider } from "../providers/wallet";
import { GetNativeBalanceParams, GetNativeBalanceSchema } from "../types/index";
import { ethers } from "ethers";

export const getNativeBalanceAction: Action = {
    name: "GET_NATIVE_BALANCE",
    similes: [
        "CHECK_COTI_BALANCE",
        "GET_COTI_BALANCE",
        "NATIVE_BALANCE",
        "BALANCE_CHECK",
        "CHECK_BALANCE",
        "SHOW_BALANCE",
        "GET_BALANCE",
        "BALANCE_OF",
        "CHECK_ADDRESS_BALANCE",
        "GET_ADDRESS_BALANCE"
    ],
    description: "Get the native COTI token balance of a COTI blockchain account. Returns the account balance in COTI tokens.",
    validate: async (runtime: IAgentRuntime, message: Memory): Promise<boolean> => {
        try {
            const text = message.content?.text || "";
            elizaLogger.info(`GET_NATIVE_BALANCE validation - text: "${text}"`);

            // Check if message is about balance checking
            const isBalanceQuery = /\b(balance|check.*balance|get.*balance|show.*balance|help.*check.*balance)\b/i.test(text);
            elizaLogger.info(`GET_NATIVE_BALANCE validation - isBalanceQuery: ${isBalanceQuery}`);

            // Check if message content has accountAddress field
            if (message.content?.accountAddress) {
                const params = GetNativeBalanceSchema.parse(message.content);
                elizaLogger.info(`GET_NATIVE_BALANCE validation - accountAddress field found: ${params.accountAddress}`);
                return ethers.isAddress(params.accountAddress);
            }

            // Check if message text contains an Ethereum address and mentions balance
            const addressMatch = text.match(/0x[a-fA-F0-9]{40}/);
            elizaLogger.info(`GET_NATIVE_BALANCE validation - addressMatch: ${addressMatch?.[0] || 'none'}`);

            if (addressMatch && isBalanceQuery) {
                const isValid = ethers.isAddress(addressMatch[0]);
                elizaLogger.info(`GET_NATIVE_BALANCE validation - address valid: ${isValid}, returning true`);
                return isValid;
            }

            // Check for general balance queries without specific address
            if (isBalanceQuery && !addressMatch) {
                try {
                    const walletProvider = initCotiWalletProvider(runtime);
                    const hasAddress = walletProvider.getAddress() !== "";
                    elizaLogger.info(`GET_NATIVE_BALANCE validation - general balance query, has wallet: ${hasAddress}`);
                    return hasAddress;
                } catch {
                    elizaLogger.info(`GET_NATIVE_BALANCE validation - wallet provider failed`);
                    return false;
                }
            }

            elizaLogger.info(`GET_NATIVE_BALANCE validation - returning false`);
            return false;
        } catch (error) {
            elizaLogger.error(`GET_NATIVE_BALANCE validation error:`, error);
            return false;
        }
    },
    handler: async (
        runtime: IAgentRuntime,
        message: Memory,
        state: State | undefined,
        options: any = {},
        callback?: HandlerCallback
    ) => {
        try {
            elizaLogger.info("Starting GET_NATIVE_BALANCE action");

            let accountAddress: string;

            // Try to get address from message content
            if (message.content?.accountAddress) {
                const params = GetNativeBalanceSchema.parse(message.content);
                accountAddress = params.accountAddress;
            } else {
                // Try to extract address from message text
                const text = message.content?.text || "";
                const addressMatch = text.match(/0x[a-fA-F0-9]{40}/);
                
                if (addressMatch) {
                    accountAddress = addressMatch[0];
                } else {
                    // Use wallet's own address
                    const walletProvider = initCotiWalletProvider(runtime);
                    accountAddress = walletProvider.getAddress();
                }
            }

            if (!ethers.isAddress(accountAddress)) {
                throw new Error("Invalid account address");
            }

            const walletProvider = initCotiWalletProvider(runtime);
            const balance = await walletProvider.getBalance(accountAddress);

            const responseText = `COTI Balance for ${accountAddress}: ${ethers.formatEther(balance)} COTI`;

            if (callback) {
                await callback({
                    text: responseText,
                    content: {
                        success: true,
                        accountAddress,
                        balance: ethers.formatEther(balance),
                        balanceWei: balance.toString(),
                    },
                });
            }

            return {
                text: responseText,
                success: true,
                data: {
                    accountAddress,
                    balance: ethers.formatEther(balance),
                    balanceWei: balance.toString(),
                },
            };
        } catch (error) {
            elizaLogger.error("Error in GET_NATIVE_BALANCE action:", error);
            const errorMessage = `Failed to get COTI balance: ${error instanceof Error ? error.message : String(error)}`;

            if (callback) {
                await callback({
                    text: errorMessage,
                    content: { success: false, error: errorMessage },
                });
            }

            return {
                text: errorMessage,
                success: false,
                error: error instanceof Error ? error : new Error(String(error)),
            };
        }
    },
    examples: [
        [
            {
                name: "{{user1}}",
                content: {
                    text: "What's my COTI balance?",
                },
            },
            {
                name: "{{user2}}",
                content: {
                    text: "I'll check your COTI balance for you. Let me retrieve your account information.",
                    action: "GET_NATIVE_BALANCE",
                },
            },
        ],
        [
            {
                name: "{{user1}}",
                content: {
                    text: "Check balance for 0x1234567890123456789012345678901234567890",
                },
            },
            {
                name: "{{user2}}",
                content: {
                    text: "I'll check the COTI balance for that address.",
                    action: "GET_NATIVE_BALANCE",
                },
            },
        ],
    ],
};
